// Topbar (56px). Collapse toggle + logo on the left, search + user on the right.
//
// Persisted collapse state lives in useCollapsedSidebar(); the Layout owns
// the boolean and passes it down so the sidebar can match. Theme state lives
// in useTheme(), owned here.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PanelLeft, Search, LogOut, Settings, Sparkles, Sun, Moon, CircleHelp } from 'lucide-react';
import { useSession, getLogoutUrl } from '@/lib/useSession';
import { useTheme } from '@/lib/useTheme';
import { CandyGlyph } from '@/components/CandyGlyph';
import { useChangelogUnread } from '@/lib/useChangelog';
import { formatBadgeCount } from '@/lib/badges';

/**
 * Account menu behind the avatar. Pattern copied from The Sweet Spot's
 * `src/shell/Header.tsx` AvatarMenu: a clickable initials circle, a w-56 panel
 * anchored bottom-right of the avatar, an identity block (name + email), a
 * hairline divider, then the actions. Outside-click closes via a full-screen
 * invisible button behind the panel, which is Sweet Spot's approach.
 *
 * Difference from Sweet Spot, on purpose: Sweet Spot's menu opens a centered
 * sign-out modal. Steven directed (2026-07-28) that sign-out move OUT of the
 * modal and INTO this menu, so both destinations are menu items here and the
 * modal is gone. Cockpit has two sign-out reaches (Cockpit only vs all of
 * Microsoft 365), and losing that choice would be a regression, so both appear
 * with their captions inline.
 *
 * Escape also closes, which Sweet Spot's avatar menu does not do (its own
 * BottomNav sheet does). The old cockpit sign-out modal closed on Escape, so
 * keeping it avoids taking a behavior away.
 *
 * Deliberately NO role="menu" / role="menuitem", matching Sweet Spot. Those roles
 * override the native link roles and promise keyboard menu semantics (arrow-key
 * navigation, focus wrap) that this component does not implement, so claiming
 * them would be worse for a screen reader than leaving the native roles alone.
 * The trigger still carries aria-label + aria-expanded.
 */
function AvatarMenu({ initials, displayName, email }: { initials: string; displayName: string; email: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const itemClass =
    'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-amp-ink hover:bg-amp-cream-warm dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        title={displayName}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-amp-olive text-amp-cream focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:bg-amp-olive-dark dark:text-amp-dark-bg"
        style={{ fontSize: 12, fontWeight: 600 }}
      >
        {initials}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute right-0 z-50 mt-2 w-64 rounded-md border border-amp-line bg-amp-white p-1.5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
          >
            <div className="px-2.5 py-2">
              <p className="truncate text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
                {displayName}
              </p>
              <p className="truncate text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {email}
              </p>
            </div>
            <div className="my-1 h-px bg-amp-line dark:bg-amp-dark-line" />
            <Link to="/changelog" onClick={() => setOpen(false)} className={itemClass}>
              <Sparkles size={14} aria-hidden /> What&apos;s new
            </Link>
            <Link to="/settings" onClick={() => setOpen(false)} className={itemClass}>
              <Settings size={14} aria-hidden /> Settings
            </Link>
            <div className="my-1 h-px bg-amp-line dark:bg-amp-dark-line" />
            <a href={getLogoutUrl(false)} className={itemClass}>
              <LogOut size={14} aria-hidden />
              <span>
                <span className="block">Sign out of Cockpit</span>
                <span
                  className="block text-amp-ink-muted dark:text-amp-dark-ink-muted"
                  style={{ fontSize: 11 }}
                >
                  Stays signed into Outlook, Teams, and other Microsoft 365 apps.
                </span>
              </span>
            </a>
            <a href={getLogoutUrl(true)} className={itemClass}>
              <LogOut size={14} aria-hidden />
              <span>
                <span className="block">Sign out of Microsoft 365</span>
                <span
                  className="block text-amp-ink-muted dark:text-amp-dark-ink-muted"
                  style={{ fontSize: 11 }}
                >
                  Signs you out of every M365 app tenant-wide. Use on a shared workstation.
                </span>
              </span>
            </a>
          </div>
        </>
      )}
    </div>
  );
}

function initialsFor(name: string, email: string): string {
  const source = name.trim() || email.trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return (parts[0] ?? '').slice(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
}

type Props = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Store-only shell: no sidebar exists, so the collapse toggle is hidden. */
  hideSidebarToggle?: boolean;
  /** Opens the Help center. Both shells pass it, so the button looks the same everywhere. */
  onHelpOpen?: () => void;
  /** Unread replies on the person's requests; shown as a badge on the Help button. */
  helpUnread?: number;
};

export function Header({
  collapsed,
  onToggleCollapsed,
  hideSidebarToggle = false,
  onHelpOpen,
  helpUnread = 0,
}: Props) {
  const { user } = useSession();
  const [theme, setTheme] = useTheme();
  const displayName = user?.name?.trim() || user?.email || 'Signed out';
  const initials = user ? initialsFor(user.name ?? '', user.email) : '--';
  const isDark = theme === 'dark';
  const unreadChangelog = useChangelogUnread();

  return (
    <header
      className="flex h-14 flex-shrink-0 items-center justify-between border-b border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface"
      style={{ padding: '0 16px 0 12px' }}
    >
      <div className="flex items-center" style={{ gap: 12 }}>
        {!hideSidebarToggle && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
        >
          <PanelLeft size={16} aria-hidden />
        </button>
        )}
        <Link
          to="/"
          className="flex items-center text-amp-ink no-underline focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 rounded-md dark:text-amp-dark-ink"
          style={{ gap: 10 }}
        >
          <span className="inline-flex text-amp-olive dark:text-amp-olive-dark">
            <CandyGlyph size={22} strokeWidth={1.7} />
          </span>
          <span
            className="text-amp-ink dark:text-amp-dark-ink"
            style={{ fontWeight: 600, fontSize: 16, letterSpacing: 0.2 }}
          >
            Cockpit
          </span>
          {!collapsed ? (
            <span
              className="hidden text-amp-ink-muted sm:inline dark:text-amp-dark-ink-muted"
              style={{ fontWeight: 400, fontSize: '12.5px' }}
            >
              Ampersand Brands intranet
            </span>
          ) : null}
        </Link>
      </div>
      <div className="flex items-center" style={{ gap: 14 }}>
        {/* Search tools box hidden until it is functional (not yet wired to a search
            modal). To restore: re-add `sm:inline-flex` to the className below and wire
            the onClick. Hidden by Steven, 2026-06-13. */}
        <button
          type="button"
          aria-label="Search tools"
          title="Search (coming soon)"
          className="hidden items-center gap-2 rounded-pill border border-amp-line bg-amp-cream py-[7px] px-[14px] text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream-warm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface"
          style={{ fontSize: 13, fontWeight: 400, minWidth: 200 }}
        >
          <Search aria-hidden size={14} />
          <span>Search tools</span>
        </button>
        {onHelpOpen && (
          <button
            type="button"
            onClick={onHelpOpen}
            aria-label={helpUnread > 0 ? `Help, ${helpUnread} unread ${helpUnread === 1 ? 'reply' : 'replies'}` : 'Help'}
            title="Help and support"
            className="relative inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
          >
            <CircleHelp size={16} aria-hidden />
            {helpUnread > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amp-olive-deep px-1 text-amp-cream"
                style={{ fontSize: 10, fontWeight: 600 }}
                aria-hidden
              >
                {formatBadgeCount(helpUnread)}
              </span>
            )}
          </button>
        )}
        <Link
          to="/changelog"
          aria-label="What's new"
          title={unreadChangelog ? "What's new (unread updates)" : "What's new"}
          className="relative inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
        >
          <Sparkles size={16} aria-hidden />
          {unreadChangelog && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-600 ring-2 ring-amp-white dark:bg-red-400 dark:ring-amp-dark-surface"
            />
          )}
        </Link>
        <button
          type="button"
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-pressed={isDark}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-md text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm"
        >
          {isDark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
        </button>
        <span
          className="hidden text-amp-ink-muted sm:inline dark:text-amp-dark-ink-muted"
          style={{ fontSize: 13 }}
        >
          {displayName}
        </span>
        {user ? (
          <AvatarMenu initials={initials} displayName={displayName} email={user.email} />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-amp-olive text-amp-cream dark:bg-amp-olive-dark dark:text-amp-dark-bg"
            style={{ fontSize: 12, fontWeight: 600 }}
            aria-hidden
          >
            {initials}
          </div>
        )}
      </div>
    </header>
  );
}
